import React from 'react'
import styled from 'styled-components/macro'
import { useField } from 'react-final-form'
import { useFieldArray } from 'react-final-form-arrays'
import {
  values,
  pathOr,
  pipe,
  take,
  drop,
  map,
  split,
  replace,
  flatten,
  path
} from 'ramda'

import { InlinePicker, InlineTypeahead } from '../../../ui'
import { QueryBuilderValue } from '../'
import { useSentence } from '../../hooks'
import { buildHelpers } from '../../utils/buildHelper'
import {
  onRemoveCleanUpFieldOr,
  onRemoveCleanUpComparisonOr,
  onRemoveCleanUpValueOr,
  onCompleteCleanUpConjunctionOr
} from '../../utils/sentenceHelper'

const Wrap = styled.div`
  padding: ${({ theme }) => theme.padding.s} 0px;
  background-color: 'red';
`

export const QueryBuilderConditionOr = ({
  input,
  parentInput,
  onCompleteWhereAndCondition,
  onCompleteWhereOrCondition,
  onComplete: onCompleteSenteceWherePiece,
  onRemoveWhereRow,
  onRemove: onRemoveSentenceWherePiece,
  onTerminate: onTerminateSenteceWherePiece,
  dataSchema,
  dataTypesSchema,
  onUpdateFormField,
  conditionInfo,
  querySentenceProps
}) => {
  const { name } = input
  const selectedObjectTypeId = pathOr(
    null,
    ['value', 'objectTypeId'],
    parentInput
  )

  const selectedDataSourceId = pathOr(
    null,
    ['value', 'dataSourceIds'],
    parentInput
  )

  const selectedFieldId = pathOr(null, ['value', 'condition', 'field'], input)
  const selectedComparisonId = pathOr(
    null,
    ['value', 'condition', 'comparison'],
    input
  )

  const {
    getFieldOptions,
    getComparisonOptions,
    getInputOptions
  } = buildHelpers(dataSchema, dataTypesSchema)

  const fieldOptions = getFieldOptions(
    selectedObjectTypeId,
    selectedDataSourceId
  )

  const comparisonOptions = getComparisonOptions(
    selectedObjectTypeId,
    selectedFieldId
  )

  const inputOptions = getInputOptions(
    selectedObjectTypeId,
    selectedFieldId,
    selectedComparisonId
  )

  const nameSplitted = splitFieldName(input.name)
  const currentGroup = path(take(2, nameSplitted), parentInput.value)

  const pieceDefinitions = {
    [`${name}.field`]: {
      field: useField(`${name}.condition.field`),
      render: ({ field, ...sentenceProps }) => (
        <InlinePicker
          options={fieldOptions}
          type="secondary"
          onCompleteCleanUp={objectTypeId =>
            parentInput.onChange({ ...parentInput.value, objectTypeId })
          }
          onRemoveCleanUp={onRemoveCleanUpFieldOr(
            parentInput,
            input,
            onRemoveSentenceWherePiece,
            onRemoveWhereRow,
            onUpdateFormField,
            currentGroup
          )}
          {...field}
          {...sentenceProps}
        />
      )
    },
    [`${name}.comparison`]: {
      field: useField(`${name}.condition.comparison`),
      render: ({ field, ...sentenceProps }) => (
        <InlinePicker
          type="transparent"
          options={comparisonOptions}
          onRemoveCleanUp={onRemoveCleanUpComparisonOr(
            input,
            onUpdateFormField
          )}
          {...field}
          {...sentenceProps}
        />
      )
    },
    [`${name}.value`]: {
      field: useFieldArray(`${name}.condition.value`),
      render: sentenceProps => (
        <QueryBuilderValue
          name={`${name}.condition.value`}
          key={`${name}.condition.value`}
          onRemoveCleanUp={onRemoveCleanUpValueOr(input, onUpdateFormField)}
          inputOptions={inputOptions}
          {...sentenceProps}
        />
      )
    },
    [`${name}.conjunction`]: {
      field: useField(`${name}.conjunction`),
      render: ({ field, ...sentenceProps }) => (
        <InlineTypeahead
          onTerminateParent={onTerminateSenteceWherePiece}
          onTerminateCleanUp={onCompleteSenteceWherePiece}
          onCompleteCleanUp={onCompleteCleanUpConjunctionOr(
            onCompleteWhereOrCondition,
            onCompleteWhereAndCondition,
            conditionInfo
          )}
          options={[
            { value: '.', label: '.', terminate: true },
            { value: 'AND', label: 'AND' },
            { value: 'OR', label: 'OR' }
          ]}
          {...field}
          {...sentenceProps}
        />
      )
    }
  }

  const sentence = useSentence({
    sentenceOrder: [
      `${name}.field`,
      `${name}.comparison`,
      `${name}.value`,
      `${name}.conjunction`
    ],
    pieceDefinitions,
    querySentenceProps
  })

  return (
    <Wrap>
      {sentence.map((piece, pieceIndex) =>
        pieceDefinitions[piece.id].render({
          ...piece,
          field: pieceDefinitions[piece.id].field,
          fields: values(pieceDefinitions).reduce(
            (acc, { id, field }) => ({ ...acc, [id]: field }),
            {}
          )
        })
      )}
    </Wrap>
  )
}

// splits name and removes audienceRequirements.queries[i] part of name
const splitFieldName = pipe(
  split('.'),
  drop(2),
  map(
    pipe(
      split('['),
      map(replace(/\]/g, ''))
    )
  ),
  flatten
)
